import type { AutoRejectEmailSettings } from '@/api/aiScoring/aiScoring.api';
import {
  AUTO_REJECT_EMAIL_DEFAULTS,
  type AutoRejectEmailFormValues,
} from '../autoRejectEmail.types';

// Raw `ai_scoring` GET shape for the rejection-email config (snake_case).
export interface AutoRejectEmailConfigResponse {
  auto_reject_send_email?: boolean;
  // The user-selected *source* template. The GET exposes this (not the
  // server-managed private clone `ats_comm_template_id`), so the select can
  // rehydrate against the source-template options list on edit.
  source_ats_comm_template_id?: number | string | null;
  options?: {
    auto_reject_schedule?: boolean | string | number | null;
    auto_reject_send_after?: number | string | null;
    auto_reject_scheduled_at?: string | null;
    auto_reject_scheduled_at_timezone?: string | null;
    auto_reject_ats_shared_email_id?: number | string | null;
    auto_reject_sender_id?: number | string | null;
    auto_reject_email_subject?: string | null;
    auto_reject_email_body?: string | null;
    auto_reject_cc?: string[] | null;
    auto_reject_bcc?: string[] | null;
  } | null;
}

interface FromUser {
  id?: number;
  email?: string;
  isSharedEmail?: boolean;
  [key: string]: unknown;
}

const toBool = (v: unknown): boolean =>
  v === true || v === 'true' || v === 1 || v === '1';

const toNum = (v: unknown): number | null =>
  v === null || v === undefined || v === '' ? null : Number(v);

// Rebuild the "From" object from a saved sender so the From field binds to it on
// edit (and isn't overwritten with the current user on save). Mirrors
// auto-rejections `resolveFromUserObject` — candidate for extraction (ENG-3114).
const resolveFromUserObject = (
  sharedEmailId: number | null,
  senderId: number | null,
  fromUsers: FromUser[]
): FromUser | null => {
  if (sharedEmailId) {
    return (
      fromUsers.find((u) => u.isSharedEmail && u.id === sharedEmailId) ?? {
        id: sharedEmailId,
        isSharedEmail: true,
      }
    );
  }
  if (senderId) {
    return fromUsers.find((u) => !u.isSharedEmail && u.id === senderId) ?? null;
  }
  return null;
};

/**
 * Maps the persisted `ai_scoring` email config back to the island form values
 * for restore-on-edit. Pass `fromUsers` (from `useGetAllUsers`) to rehydrate the
 * sender so the From field binds to it and isn't overwritten on save.
 */
export function mapConfigToFormValues(
  aiScoring?: AutoRejectEmailConfigResponse | null,
  fromUsers: FromUser[] = []
): AutoRejectEmailFormValues {
  const opts = aiScoring?.options ?? {};
  const templateId = aiScoring?.source_ats_comm_template_id;
  const fromUserObject = resolveFromUserObject(
    toNum(opts.auto_reject_ats_shared_email_id),
    toNum(opts.auto_reject_sender_id),
    fromUsers
  );
  return {
    ...AUTO_REJECT_EMAIL_DEFAULTS,
    sendRejectEmail: toBool(aiScoring?.auto_reject_send_email),
    selectedTemplate:
      templateId !== null && templateId !== undefined
        ? { id: Number(templateId) }
        : null,
    from: fromUserObject?.email ?? '',
    fromUserObject,
    subject: opts.auto_reject_email_subject ?? '',
    body: opts.auto_reject_email_body ?? '',
    cc: opts.auto_reject_cc ?? [],
    bcc: opts.auto_reject_bcc ?? [],
    scheduleEmail: toBool(opts.auto_reject_schedule),
    sendAfter:
      opts.auto_reject_send_after !== null &&
      opts.auto_reject_send_after !== undefined
        ? Number(opts.auto_reject_send_after)
        : AUTO_REJECT_EMAIL_DEFAULTS.sendAfter,
    scheduleTime: opts.auto_reject_scheduled_at ?? null,
    scheduleTimezone: opts.auto_reject_scheduled_at_timezone
      ? {
          key: opts.auto_reject_scheduled_at_timezone,
          label: opts.auto_reject_scheduled_at_timezone,
        }
      : null,
  };
}

/**
 * Maps the auto-reject email "island" form values to the `ai_scoring_attributes`
 * email payload (ENG-3114). Sender resolution follows the shared-vs-personal
 * rule (ENG-3091): a shared-email sender goes to `ats_shared_email_id`, a
 * personal user to `sender_id`. When "send email" is off, only the flag is sent
 * so the backend clears any prior config.
 */
export function mapFormValuesToEmailSettings(
  values: AutoRejectEmailFormValues
): AutoRejectEmailSettings {
  if (!values.sendRejectEmail) {
    return { auto_reject_send_email: false };
  }

  const template = values.selectedTemplate as { id?: number } | null;
  const fromUser = values.fromUserObject as {
    id?: number;
    isSharedEmail?: boolean;
  } | null;
  const isSharedEmail = !!fromUser?.isSharedEmail;
  const scheduled = !!values.scheduleEmail;
  const timezone =
    (values.scheduleTimezone as { key?: string } | null)?.key ?? null;

  return {
    auto_reject_send_email: true,
    source_ats_comm_template_id: template?.id ?? null,
    auto_reject_email_subject: values.subject || null,
    auto_reject_email_body: values.body || null,
    auto_reject_sender_id: isSharedEmail ? null : (fromUser?.id ?? null),
    auto_reject_ats_shared_email_id: isSharedEmail
      ? (fromUser?.id ?? null)
      : null,
    auto_reject_schedule: scheduled,
    auto_reject_send_after: scheduled ? values.sendAfter : null,
    auto_reject_scheduled_at: scheduled ? values.scheduleTime : null,
    auto_reject_scheduled_at_timezone: scheduled ? timezone : null,
    auto_reject_cc: values.cc ?? [],
    auto_reject_bcc: values.bcc ?? [],
  };
}
